import { Timer, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";

import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import useTranslations from "@/shared/lang/useTranslations";

import type { OtcTimingEstimate } from "../../types/cryptoSwaps.types";
import { estimateMinutes, formatCountdown } from "../../utils/cryptoSwapFormat";

export interface OperationEtaProps {
  /** When the wait started: the order for a purchase, the deposit received for a sale. */
  since: string | null;
  /** Without one only the elapsed time is shown. */
  estimate: OtcTimingEstimate | null | undefined;
}

/** The elapsed time of a running operation against how long recent ones took. */
export function OperationEta({ since, estimate }: OperationEtaProps) {
  const { t } = useTranslations("cryptoSwaps");
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const start = since ? Date.parse(since) : Number.NaN;
  if (Number.isNaN(start)) return null;

  const elapsedMs = Math.max(0, now - start);
  const estimatedMs = estimate ? estimate.estimatedSeconds * 1000 : null;
  const late = estimatedMs !== null && elapsedMs > estimatedMs;
  const remainingMs = estimatedMs !== null ? estimatedMs - elapsedMs : null;

  return (
    <div className="flex flex-col gap-2.5 rounded-2xl bg-muted p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
          <Timer aria-hidden="true" className="size-4 text-muted-foreground" />
          {estimate
            ? t("eta_estimate", { minutes: estimateMinutes(estimate.estimatedSeconds) })
            : t("eta_running")}
        </span>
        {remainingMs !== null && remainingMs > 0 ? (
          <span className="font-mono text-lg font-semibold text-foreground tabular-nums">
            {t("eta_remaining", { time: formatCountdown(remainingMs) })}
          </span>
        ) : (
          <span className="font-mono text-sm text-muted-foreground tabular-nums">
            {t("eta_elapsed", { time: formatCountdown(elapsedMs) })}
          </span>
        )}
      </div>
      {estimatedMs !== null && (
        <ColocaProgress
          size="sm"
          value={Math.min(100, (elapsedMs / estimatedMs) * 100)}
          tone={late ? "warning" : "primary"}
          aria-label={t("eta_progress_label")}
        />
      )}
      {late && (
        <p role="status" className="m-0 text-[13px] leading-relaxed text-foreground">
          {t("eta_late")}
        </p>
      )}
      {!late && estimate?.trend === "slower" && (
        <p className="m-0 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <TrendingUp aria-hidden="true" className="size-3.5" />
          {t("eta_trend_slower")}
        </p>
      )}
    </div>
  );
}

/** One line for a confirmation: how long it should take, and whether it is running slower than usual. */
export function EstimateLine({ estimate }: { estimate: OtcTimingEstimate | null | undefined }) {
  const { t } = useTranslations("cryptoSwaps");
  if (!estimate) return null;
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <span>{t("eta_short", { minutes: estimateMinutes(estimate.estimatedSeconds) })}</span>
      {estimate.trend === "slower" && (
        <span className="text-xs text-warning">{t("eta_trend_slower_short")}</span>
      )}
    </span>
  );
}
